import { Loader2Icon, MicIcon, SquareIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib';
import type { FolioSpeechToTextState } from './speech-to-text-context';

interface MicButtonProps {
  state: FolioSpeechToTextState;
  onStart: () => void;
  onStop: () => void;
}

export function MicButton({ state, onStart, onStop }: MicButtonProps) {
  const { t } = useTranslation();
  const recording = state === 'recording';
  const requesting = state === 'requesting';
  const label = recording
    ? t('speechToText.stop', 'Stop dictation')
    : t('speechToText.start', 'Start dictation');

  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={recording}
      disabled={requesting}
      onClick={recording ? onStop : onStart}
      className={cn(
        'relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
        'transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2',
        'focus-visible:ring-ring focus-visible:ring-offset-1 disabled:cursor-not-allowed',
        recording
          ? 'bg-red-500 text-white shadow-sm hover:bg-red-600'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      {/* Pulsing ring while recording — the "I'm listening" cue. */}
      {recording && (
        <span className="absolute inset-0 animate-ping rounded-full bg-red-500/40" aria-hidden />
      )}
      {requesting ? (
        <Loader2Icon className="h-4 w-4 animate-spin" />
      ) : recording ? (
        <SquareIcon className="h-3.5 w-3.5 fill-current" />
      ) : (
        <MicIcon className="h-4 w-4" />
      )}
    </button>
  );
}
